import { useEffect } from "react";
import cn from "clsx";
import Api from "@middleware/api";
import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { Trans, useTranslation } from "next-i18next";
import {
  useGlobalMessaging,
  useSchemaMessages,
  useUI,
} from "@middleware/hooks";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  EFieldTypes,
  EGender,
  ICountry,
  SelectValue,
  TFunctionFieldProps,
  TGender,
  TResponseResultBilan,
  TResultBilan,
  TResultsBilanData,
} from "@middleware/types";
import { Content } from "@prismicio/client";
import { Button, Link } from "@components/ui";
import {
  BILAN_DATA,
  BILAN_GENDER_MAN_VALUE,
  BILAN_GENDER_WOMAN_VALUE,
  CLIENT_IP,
  RESULT_BILAN_DATA,
  URL_PAGE_CGV,
  URL_PAGE_DURATION,
} from "@middleware/constants";
import Field from "@components/ui/Forms/Field/Field";
import {
  serializeResultBilan,
  serializeResultBilanData,
} from "@middleware/helpers/global/bilan";
import { useRouter } from "next/router";
import {
  getFromStorage,
  getUtmSessions,
  setToStorage,
} from "@middleware/helpers/global/sessions";
import { pushGuestDataLayer } from "@middleware/helpers";
import { fields } from "./fields";
import { defaultValues } from "./defaultValues";
import schema from "./schema";
import style from "../Bilan.module.scss";
import "react-phone-input-2/lib/style.css";

interface Props {
  questions: Content.BilanBlockSlice;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  countries: ICountry[];
  phoneIsRequired?: boolean;
  userEmail: string;
  gender?: TGender;
}

export const BilanForm = ({
  questions,
  currentStep,
  setCurrentStep,
  countries,
  phoneIsRequired,
  userEmail,
  gender,
}: Props) => {
  const { t } = useTranslation();
  const { setMessage } = useGlobalMessaging();
  const { showLoader, hideLoader } = useUI();
  const customSchema = schema(useSchemaMessages(), phoneIsRequired);
  const lastStep = fields(gender).length - 1;
  const router = useRouter();
  const storedBilanData = getFromStorage(BILAN_DATA) as FieldValues | null;
  if (storedBilanData != null) {
    storedBilanData.birth_date = new Date(storedBilanData.birth_date);
  }
  if (gender === EGender.MAN) {
    defaultValues.gender = BILAN_GENDER_MAN_VALUE;
  } else if (gender === EGender.WOMAN) {
    defaultValues.gender = BILAN_GENDER_WOMAN_VALUE;
  }
  const defaultValuesFromStorage =
    storedBilanData ?? (defaultValues as FieldValues);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, isSubmitted, errors },
    reset,
    control,
    setValue,
  } = useForm({
    mode: "onBlur",
    defaultValues: defaultValuesFromStorage,
    resolver: yupResolver<FieldValues>(customSchema),
  });

  useEffect(() => {
    if (userEmail !== "") {
      setValue("email", userEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  useEffect(() => {
    if (isSubmitted) {
      const keys = Object.keys(defaultValues);
      const currentStepKey = keys[currentStep];
      if (currentStepKey in errors) return;
      goNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitted, errors]);

  const getFieldProps = ({ field }: TFunctionFieldProps) => {
    // @ts-expect-error
    const label = questions.primary[field.name] ?? "";
    const labelPhone = label + "<span>" + t("bilan.optional") + "<span>";
    const readonly = field.name === "email" && userEmail !== "" ? true : false;
    const fieldProps = {
      name: field.name,
      label: field.name === "phone" ? labelPhone : label,
      className: field.class,
      placeholder: t(field.placeholder ?? ""),
      register: register,
      readonly: readonly,
      control: control,
      type: field.type,
      values: field.options
        ? field.options.map((option: SelectValue) => {
            return {
              label: t(option.label),
              value: option.value,
            };
          })
        : [],
      error: errors[field.name]?.message as string,
      countryCode:
        field.type === EFieldTypes.TEL
          ? countries[0].code.toLocaleLowerCase()
          : "",
      availableCountries:
        field.type === EFieldTypes.TEL
          ? countries.map((country) => country.code.toLocaleLowerCase())
          : undefined,
      marks: field.marks,
      maxDate: field.maxDate ?? undefined,
      min: field.min,
      defaultValue: field.defaultValue!,
      startLabel: t(field.startLabel ?? ""),
      endLabel: t(field.endLabel ?? ""),
    };

    return fieldProps;
  };

  const goNext = () => {
    if (currentStep < lastStep) {
      setCurrentStep(currentStep + 1);
      reset({}, { keepErrors: false, keepValues: true });
    }
  };
  const goPrev = () => {
    setCurrentStep(currentStep - 1);
  };
  // Submit form
  const onSubmit: SubmitHandler<FieldValues> = async (datas) => {
    // stop submit before last step
    if (currentStep < lastStep) return;

    showLoader();
    const bilanResults = serializeResultBilanData(datas);
    const data: TResultsBilanData = {
      evaluation: JSON.stringify(bilanResults),
      email: datas.email,
      firstName: datas.name,
      phoneNumber: datas.phone,
    };
    const utmParams = getUtmSessions();

    const response = await Api.bilan.saveBilanResult(data, {
      ...utmParams,
      ip_address: getFromStorage(CLIENT_IP) ?? "",
    });
    if (response.status === 200) {
      setToStorage(BILAN_DATA, datas);

      const responseData = serializeResultBilan(
        response.data as TResponseResultBilan,
      ) as TResultBilan;
      setToStorage(RESULT_BILAN_DATA, responseData);

      pushGuestDataLayer(response.data.customerId);
      await router.push(URL_PAGE_DURATION);
    } else {
      setMessage({
        text: response.data.title,
        type: "ERROR",
      });
    }

    hideLoader();
  };

  return (
    <div className={style.blocForm}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="bilan-form">
          {fields(gender).map((field, stepIndex) => {
            const fieldProps = getFieldProps({
              field,
              register,
              errors,
              control,
            });

            return (
              currentStep === stepIndex && (
                <div
                  className={cn(
                    style.filedRow,
                    style[field.name],
                    style[field.type],
                    {
                      [style.error]: !!errors["birth_date"],
                      [style.defaultToMan]: gender === EGender.MAN,
                    },
                  )}
                  key={stepIndex}
                >
                  <Field variant={field.type} props={fieldProps} />
                </div>
              )
            );
          })}

          {currentStep === lastStep && (
            <div className={cn("text-center", style.autorisation)}>
              <Field
                variant="checkbox"
                props={{
                  type: "checkbox",
                  name: "agreementCheck",
                  control: control,
                  register: register,
                  label: (
                    <Trans
                      t={t}
                      i18nKey={"bilan.allowedUse"}
                      components={{
                        url: <Link href={URL_PAGE_CGV} className="simple" />,
                      }}
                    />
                  ),
                  error: errors["agreementCheck"]?.message as string,
                }}
              />
            </div>
          )}

          <div
            className={cn(style.action, {
              [style.last]: currentStep === lastStep,
            })}
          >
            {currentStep !== lastStep && currentStep > 0 && (
              <Button
                type="button"
                className={style.btnPrev}
                variant="primary"
                onClick={() => goPrev()}
              >
                {t("buttons.prev")}
              </Button>
            )}
            <Button
              type="submit"
              className={style.btnNext}
              variant="primary"
              disabled={isSubmitting}
            >
              {currentStep === lastStep
                ? t("buttons.discoverResults")
                : t("buttons.next")}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};
